import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TimeSeriesChart } from './TimeSeriesChart';
import { MetricCalculationsResult, useMetricCalculations } from '@/hooks/useMetricCalculations';
import { MetricTimeseriesData } from '@/services/MetricsService';

type TimeRangeLevel = 'week' | 'month' | 'threeMonths' | 'sixMonths' | 'year';

interface Metric {
  "ValueStream Stage": string;
  "Business Capability": string;
  "KPI Name": string;
  "KPI Description": string;
  "KPI Formula": string;
  "Operation": string;
  "metricOperation"?: string;
  "metricValueKey": string;
  "target"?: Array<{ startTs: number; endTs: number; value: number }> | null;
  "decreasingTarget"?: boolean | null;
  "alertingThresholdPercentage"?: number | null;
  "metricDimension"?: string;
}

interface MetricCardProps {
  metric: Metric;
  calculations: MetricCalculationsResult;
  isLoading: boolean;
  showKPIMode: boolean;
  timeRange: TimeRangeLevel;
  effectiveTarget?: Array<{ startTs: number; endTs: number; value: number }> | null;
}

export function MetricCard({ 
  metric, 
  calculations,
  isLoading, 
  showKPIMode,
  timeRange,
  effectiveTarget,
}: MetricCardProps) {
  const {
    chartData,
    currentValue,
    hasChartData,
    targetProgress,
  } = calculations;

  const formatValue = (value: number | string | Record<string, number>): string => {
    if (value === 'N/A') return 'N/A';
    
    // If the value is a Record (for breakdowns like revenue_by_source), sum it up
    if (typeof value === 'object') {
      const sum = Object.values(value).reduce((acc, val) => acc + val, 0);
      value = sum;
    }
    
    // Based on operation type, format the value
    switch(metric.Operation.toLowerCase()) {
      case 'percentage':
        return `${Number(value).toFixed(1)}%`;
      case 'count':
        return Number(value).toLocaleString();
      case 'average':
        return Number(value).toLocaleString(undefined, { 
          minimumFractionDigits: 0,
          maximumFractionDigits: 1
        });
      default:
        return Number(value).toLocaleString(undefined, { 
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
    }
  };

  // Helper to format breakdown data for display
  const formatBreakdownData = (data: Record<string, number> | string): React.ReactNode => {
    if (typeof data === 'string' || data === null) return null;
    
    // Sort by value (descending)
    const sortedData = Object.entries(data)
      .sort(([, valueA], [, valueB]) => valueB - valueA);
    
    // Get top 5 items
    const topItems = sortedData.slice(0, 5);
    
    return (
      <div className="mt-2 space-y-1">
        {topItems.map(([key, value]) => (
          <div key={key} className="flex justify-between text-xs">
            <span className="text-muted-foreground">{key}</span>
            <span className="font-medium">{formatValue(value)}</span>
          </div>
        ))}
        {sortedData.length > 5 && (
          <div className="text-xs text-muted-foreground text-right">
            + {sortedData.length - 5} more
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="overflow-hidden h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge variant="outline" className="bg-primary/5">
                {metric["ValueStream Stage"]}
              </Badge>
              <Badge variant="secondary" className="bg-secondary/20">
                {metric["Business Capability"]}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{metric["KPI Name"]}</CardTitle>
              {metric["Operation"] && (
                <Badge variant="outline" className="text-xs bg-muted">
                  {metric["Operation"]}
                </Badge>
              )}
            </div>
          </div>
          {hasChartData && !isLoading && !showKPIMode && targetProgress && (
            <div className="flex items-center">
              <span className={`text-lg whitespace-nowrap ${targetProgress.isOnTrack ? 'text-green-600' : 'text-red-600'}`}>
                {targetProgress.percentage}% of target
              </span>
            </div>
          )}
        </div>
        <CardDescription className="line-clamp-2 h-10">
          {metric["KPI Description"]}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-grow pb-6">
        <div className="flex flex-col h-full">
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-pulse text-center">
                <div className="h-4 w-24 bg-gray-200 rounded mb-2 mx-auto"></div>
                <div className="h-2 w-16 bg-gray-200 rounded mx-auto"></div>
              </div>
            </div>
          ) : showKPIMode ? (
            <div className="pt-2">
              {metric.metricValueKey && (
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  {metric.metricValueKey}
                </div>
              )}
              {typeof currentValue === 'object' ? (
                formatBreakdownData(currentValue as Record<string, number>)
              ) : (
                <div className="flex flex-col items-center justify-center h-[280px]">
                  <div className="text-4xl font-bold mb-2">{formatValue(currentValue)}</div>
                  <div className="text-sm text-muted-foreground">
                    {timeRange === 'threeMonths' ? '3 Month' :
                      timeRange === 'sixMonths' ? '6 Month' :
                      timeRange.charAt(0).toUpperCase() + timeRange.slice(1)} View
                  </div>
                </div>
              )}
            </div>
          ) : hasChartData ? (
            <>
              <div className="h-[280px]">
                {metric.metricValueKey && (
                  <div className="text-xs font-medium text-muted-foreground mb-2">
                    {metric.metricValueKey}
                  </div>
                )}
                <TimeSeriesChart
                  metricKey={metric.metricValueKey}
                  title={metric["KPI Name"]}
                  description=""
                  data={chartData}
                  compact={true}
                  targetArray={(() => {
                    const target = (effectiveTarget !== undefined ? effectiveTarget : metric.target) ?? undefined;
                    if (metric.metricValueKey === 'leads_by_orgunit') {
                      console.debug(`[leads_by_orgunit MetricCard] targetArray for chart: effectiveTarget defined=%s, using effectiveTarget=%s, segments=%d, first 3:`,
                        effectiveTarget !== undefined, effectiveTarget !== undefined && effectiveTarget !== null, target?.length ?? 0, target?.slice(0, 3));
                    }
                    return target;
                  })()}
                  metricOperation={metric.metricOperation || metric.Operation}
                  decreasingTarget={metric.decreasingTarget}
                  alertingThresholdPercentage={metric.alertingThresholdPercentage}
                />
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-muted-foreground text-sm">
              No data available
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="pt-4 pb-3 flex-shrink-0 flex flex-col items-start">
        {/* Legend above Formula */}
        <div className="flex flex-wrap items-center gap-4 text-xs w-full">
          {/* Value legend */}
          <span className="flex items-center gap-1">
            <svg width="24" height="8" viewBox="0 0 24 8">
              <rect x="0" y="0" width="24" height="8" rx="1" fill="#7C86F8" fillOpacity={0.7} />
            </svg>
            <span>Value</span>
          </span>

          {/* Show cumulative and target only when applicable */}
          {((metric.metricOperation || metric.Operation || '').toLowerCase() === 'average' ||
            (metric.metricOperation || metric.Operation || '').toLowerCase() === 'total') && (
            <>
              <span className="flex items-center gap-1">
                <svg width="24" height="8" viewBox="0 0 24 8">
                  <line x1="0" y1="4" x2="24" y2="4" stroke="#82ca9d" strokeWidth={2.5} />
                </svg>
                <span>Cumulative (Year)</span>
              </span>

              {metric.target && metric.target.length > 0 && (
                <span className="flex items-center gap-1">
                  <svg width="24" height="8" viewBox="0 0 24 8">
                    <line x1="0" y1="4" x2="24" y2="4" stroke="#ff7300" strokeWidth={1.5} strokeDasharray="3 3" />
                  </svg>
                  <span>Target</span>
                </span>
              )}
            </>
          )}
        </div>
        <div className="w-full mt-2">
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold">Formula:</span>
            <code className="ml-1 px-1.5 py-0.5 rounded text-xs font-mono">
              {metric["KPI Formula"]}
            </code>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}
interface MetricCardWithCalculationsProps {
  metric: Metric;
  timeseriesData: MetricTimeseriesData | null;
  timeRange: TimeRangeLevel;
  isLoading: boolean;
  showKPIMode: boolean;
  dimensionItemName?: string;
}

export function MetricCardWithCalculations({
  metric,
  timeseriesData,
  timeRange,
  isLoading,
  showKPIMode,
  dimensionItemName,
}: MetricCardWithCalculationsProps) {
  const calculations = useMetricCalculations({
    metricValueKey: metric.metricValueKey,
    operation: metric.Operation,
    metricOperation: metric.metricOperation,
    target: metric.target,
    decreasingTarget: metric.decreasingTarget,
    alertingThresholdPercentage: metric.alertingThresholdPercentage,
    timeseriesData,
    dimensionItemName,
  });

  return (
    <MetricCard
      metric={metric}
      calculations={calculations}
      isLoading={isLoading}
      showKPIMode={showKPIMode}
      timeRange={timeRange}
    />
  );
}